package com.tregz.tremenz.ui.page

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.ui.component.Title
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText

/** Where a booking request lands. The static site's `booking.php` mailed the same address. */
const val CONTACT_ADDRESS = "jerome@tremenz.com"

/**
 * Web exclusive booking form.
 *
 * Firebase Hosting is static only, so the PHP endpoints the old site posted to cannot run there.
 * The request is handed to the platform as a `mailto:` link instead, which needs no backend.
 */
@Composable
fun PageContact(
    onOpenLink: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val scheme = MaterialTheme.colorScheme

    // Typed text survives a tab switch, closing Info and a rotation.
    var name by rememberSaveable { mutableStateOf("") }
    var email by rememberSaveable { mutableStateOf("") }
    var message by rememberSaveable { mutableStateOf("") }

    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedBorderColor = scheme.primary,
        unfocusedBorderColor = scheme.outline,
        focusedLabelColor = scheme.primary,
        unfocusedLabelColor = scheme.onSurfaceVariant,
        focusedTextColor = scheme.onSurface,
        unfocusedTextColor = scheme.onSurface,
        cursorColor = scheme.primary
    )

    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
    ) {
        Title(
            title = labelText(LabelKey.ROUTE_CONTACT),
            subtitle = labelText(LabelKey.HEADING_CONTACT_SUBTITLE)
        )

        Card(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 6.dp),
            colors = CardDefaults.cardColors(containerColor = scheme.surface),
            shape = MaterialTheme.shapes.medium
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(labelText(LabelKey.CONTACT_NAME_LABEL)) },
                    singleLine = true,
                    colors = fieldColors,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = email,
                    onValueChange = { email = it },
                    label = { Text(labelText(LabelKey.CONTACT_EMAIL_LABEL)) },
                    singleLine = true,
                    colors = fieldColors,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = message,
                    onValueChange = { message = it },
                    label = { Text(labelText(LabelKey.CONTACT_MESSAGE_LABEL)) },
                    colors = fieldColors,
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 120.dp)
                )
                Spacer(modifier = Modifier.height(16.dp))
                Button(
                    onClick = { onOpenLink(bookingMailto(name, email, message)) },
                    enabled = message.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = scheme.primary,
                        contentColor = scheme.onPrimary
                    ),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(labelText(LabelKey.CONTACT_SUBMIT))
                }
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    text = labelText(LabelKey.CONTACT_MAILTO_NOTE).replace("%s", CONTACT_ADDRESS),
                    color = scheme.onSurfaceVariant,
                    style = MaterialTheme.typography.bodySmall
                )
                }
            }
        }
    }

/** Builds the `mailto:` URL that replaces the old `booking.php` POST. */
internal fun bookingMailto(name: String, email: String, message: String): String {
    val subject = "Booking request" + if (name.isBlank()) "" else " from $name"
    val body = buildString {
        if (name.isNotBlank()) appendLine("Name: $name")
        if (email.isNotBlank()) appendLine("Email: $email")
        if (isNotEmpty()) appendLine()
        append(message)
    }
    // Percent-encoding triples the size of non-ASCII text and mail apps refuse very long links.
    val capped = body.take(MAX_BODY_CHARS)
    return "mailto:$CONTACT_ADDRESS?subject=${percentEncode(subject.take(MAX_SUBJECT_CHARS))}&body=${percentEncode(capped)}"
}

private const val MAX_SUBJECT_CHARS = 120
private const val MAX_BODY_CHARS = 1_500

private const val UNRESERVED = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_.~"
private const val HEX = "0123456789ABCDEF"

/**
 * RFC 3986 percent encoding over UTF-8. `java.net.URLEncoder` is JVM only and it encodes spaces as
 * `+`, which mail clients show literally in a subject line, so common code does it by hand.
 */
private fun percentEncode(value: String): String = buildString {
    for (byte in value.encodeToByteArray()) {
        val code = byte.toInt() and 0xFF
        val char = code.toChar()
        if (char in UNRESERVED) {
            append(char)
        } else {
            append('%').append(HEX[code shr 4]).append(HEX[code and 0x0F])
        }
    }
}
